// src/components/Header/index.tsx

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LoginOutlined,
  LogoutOutlined,
  UnorderedListOutlined,
  DatabaseOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { MenuProps, Menu, Dropdown, Space, Switch } from "antd";

import { isDevelopment, GRAFANA_WORKSPACE_BASE_URL, ENVIRONMENT } from "const";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import SupportMenu from "../SupportMenu";
import Button from "common/Button";
import { useLogin } from "hooks/useLogin";

import "./style.scss";
import { getSagemakerDomainByDisplayName } from "utils/utils";

const Header: React.FC = () => {
  const msalInstance = useMsal();
  const { login, logout } = useLogin(msalInstance);

  const handleLogin = useCallback(() => login({ reloadPage: false }), [login]);

  const [current, setCurrent] = useState("");
  const [switchOn, setSwitchOn] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const accountName: string | undefined = msalInstance.accounts[0]?.name;
  const location = useLocation();

  // Keep the header menu in sync with the URL
  useEffect(() => {
    if (location.pathname === "/") {
      current !== "list" && setCurrent("list");
    } else {
      setCurrent("");
    }
  }, [location.pathname, current]);

  const susLinkgen = () => {
    const domain_id = getSagemakerDomainByDisplayName("Common");
    let link = "";
    if (domain_id != null) {
      link = "https://" + domain_id + ".sagemaker.us-east-1.on.aws/projects";
    }
    return link;
  };


  const menuItemsSwitch: MenuProps["onClick"] = (event) => {
    setCurrent(event.key);
  };

  // Dropdown menu, including our V1 switch
  const accountMenu = useMemo(
    () => (
      <Menu
        items={[
          {
            key: "switch-version",
            className: "header-switch-version",
            label: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: 200,
                  padding: "4px 8px",
                }}
              >
                <Switch
                  size="small"
                  checked={switchOn}
                  onChange={(checked) => {
                    if (checked) {
                      // navigate in the same tab:
                      window.location.href =
                        "https://app.nexus-dev.ph-dse.int.bayer.com/";
                    }
                    // reset the switch back off
                    setSwitchOn(false);
                  }}
                />
              </div>
            ),
          },
          { type: "divider" },
          {
            key: "logout",
            className: "header-logout",
            label: (
              <div onClick={logout} className="header-logout-btn">
                <LogoutOutlined className="logout-icon" />
                Log out
              </div>
            ),
          },
        ]}
      />
    ),
    [logout, switchOn],
  );

  return (
    <header
      className={`header ${isDevelopment || isAuthenticated ? "" : "header-auth"
        }`}
    >
      <div className="header-left">
        <Link to="/home" className="logo">
          PH DSE Nexus
        </Link>
        {(isDevelopment || isAuthenticated) && (
          <>
            <Menu
              onClick={menuItemsSwitch}
              selectedKeys={[current]}
              mode="horizontal"
              disabledOverflow
              className="header-menu"
              items={[
                {
                  key: "list",
                  icon: <UnorderedListOutlined className="menu-icon" />,
                  className: "menu-item",
                  label: <Link to="/">HOME</Link>,
                },
              ]}
            />
            <Menu
              selectedKeys={[current]}
              mode="horizontal"
              disabledOverflow
              className="header-menu"
              items={[
                {
                  key: "list",
                  icon: <DatabaseOutlined />,
                  className: "menu-item",
                  label: <a href={susLinkgen()} target="_blank" rel="noopener noreferrer">Sagemaker Projects</a>,
                },
              ]}
            />
            <Menu
              selectedKeys={[current]}
              mode="horizontal"
              disabledOverflow
              className="header-menu"
              items={[
                {
                  key: "cost-dashboard",
                  icon: <DollarOutlined />
                  ,
                  className: "menu-item",
                  label: (
                    <a
                      href={`${GRAFANA_WORKSPACE_BASE_URL}/login`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cost Dashboard
                    </a>
                  ),
                },
              ]}
            />
          </>
        )}
      </div>

      <div className="header-right">
        {isDevelopment || isAuthenticated ? (
          <>
            <SupportMenu />
            <Dropdown overlay={accountMenu} trigger={["click"]} arrow>
              <div className="header-account">
                <Space>{accountName}</Space>
              </div>
            </Dropdown>
          </>
        ) : (
          <Button
            icon={<LoginOutlined />}
            onClick={handleLogin}
            type="primary"
            size="large"
          >
            Login
          </Button>
        )}
      </div>
    </header>
  );
};

export default memo(Header);
